import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { PartnerSettings, PartnerSyncRun } from 'src/app/interfaces/partner-settings.interface';
import { RbacPermissionsSelectorComponent } from 'src/app/account/rbac-roles/rbac-permissions-selector/rbac-permissions-selector.component';

/**
 * The editable part of the partner settings (the PUT body).
 */
interface PartnerSettingsForm {
  can_provision_accounts: boolean;
  partner_role_name: string;
  partner_account_id: number | null;
  rbac_permission_ids: number[];
}

/**
 * Super admin Partner tab on admin/users/:id/edit. Turns partner provisioning on or off, names the
 * partner role, pins the partner's own account, edits the permission template, queues a resync and
 * shows the newest sync run. Every action is super admin only (the admin routes already are).
 */
@Component({
  selector: 'app-users-partner',
  templateUrl: './users-partner.component.html',
  styleUrls: ['./users-partner.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UsersPartnerComponent implements OnInit, OnDestroy {
  app: AppService = inject(AppService);
  private api: ApiRequestService = inject(ApiRequestService);
  utils: UtilsService = inject(UtilsService);
  rbac: RBACService = inject(RBACService);

  @Input() userId: number;

  // The template-driven controls, used to show the API's 422 messages as field errors.
  @ViewChild('roleNameModel') roleNameModel: NgModel;
  @ViewChild('ownAccountModel') ownAccountModel: NgModel;

  // The settings as last returned by the API.
  partner: PartnerSettings = null;

  // The working copy the super admin edits, and the loaded copy it is compared with.
  form: PartnerSettingsForm = this.emptyForm();
  saved: PartnerSettingsForm = this.emptyForm();

  // The API's 422 field messages, keyed by field.
  apiErrors: { [key: string]: string } = {};

  // The rows of the last sync run's totals table, built when the run changes.
  totalsRows: { label: string; value: string }[] = [];

  inProgress: boolean = false;

  // Polling state (3 second setTimeout loop, capped at 200 polls).
  private pollTimer: any = null;
  private pollCount: number = 0;
  private readonly pollIntervalMs: number = 3000;
  private readonly maxPolls: number = 200;
  private destroyed: boolean = false;

  ngOnInit(): void {
    if ( !this.guardSuperAdmin() ) {
      return;
    }
    this.getPartnerSettings();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopPolling();
  }

  /**
   * Layer 2: only super admins may manage partner settings.
   */
  private guardSuperAdmin(): boolean {
    if ( !this.app.user?.is_sitesoft_admin ) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to manage partner settings.');
      return false;
    }
    return true;
  }

  private emptyForm(): PartnerSettingsForm {
    return {
      can_provision_accounts: false,
      partner_role_name: '',
      partner_account_id: null,
      rbac_permission_ids: []
    };
  }

  /**
   * Load the partner settings.
   */
  private getPartnerSettings(): void {
    this.api
      .makeRequest('get', `v2/admin/users/${this.userId}/partner`)
      .then((response: PartnerSettings): void => {
        this.applyResponse(response, true);
      })
      .catch((errorResponse: any): void => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Store an API response. When resetForm is true the working copy and the loaded copy are rebuilt
   * from it (load, save and resync responses); polls only refresh the counts and the last run.
   */
  private applyResponse(response: PartnerSettings, resetForm: boolean): void {
    this.partner = response;
    this.buildTotalsRows();

    if ( resetForm ) {
      const ids: number[] = [...(response.rbac_permission_ids || [])];
      this.saved = {
        can_provision_accounts: !!response.can_provision_accounts,
        partner_role_name: response.partner_role_name || '',
        // Prefill: the pinned own account, else the employer (api-spec 4.8 and 9.1).
        partner_account_id: response.partner_account_id ?? response.employer_account?.id ?? null,
        rbac_permission_ids: ids
      };
      this.form = {
        ...this.saved,
        rbac_permission_ids: [...ids]
      };
      this.apiErrors = {};
    }

    this.pollCount = 0;
    this.schedulePoll();
  }

  /**
   * Whether the working copy differs from the loaded copy.
   */
  get isDirty(): boolean {
    const sameIds = (a: number[], b: number[]): boolean => {
      if ( a.length !== b.length ) {
        return false;
      }
      const sortedA: number[] = [...a].sort((x: number, y: number): number => x - y);
      const sortedB: number[] = [...b].sort((x: number, y: number): number => x - y);
      return sortedA.every((id: number, index: number): boolean => id === sortedB[index]);
    };

    return this.form.can_provision_accounts !== this.saved.can_provision_accounts
      || (this.form.partner_role_name || '').trim() !== (this.saved.partner_role_name || '').trim()
      || this.normaliseAccountId(this.form.partner_account_id) !== this.normaliseAccountId(this.saved.partner_account_id)
      || !sameIds(this.form.rbac_permission_ids, this.saved.rbac_permission_ids);
  }

  /**
   * Whether the newest sync run is still queued or running.
   */
  get runActive(): boolean {
    const status: string = this.partner?.last_run?.status;
    return status === 'queued' || status === 'running';
  }

  /**
   * Whether the employer is not the pinned own account (the saved state).
   */
  get employerMismatch(): boolean {
    return !!this.partner?.employer_account
      && this.partner.employer_account.id !== this.partner.partner_account_id;
  }

  /**
   * The label of an own account kind.
   */
  getOwnAccountKindLabel(kind: string): string {
    switch ( kind ) {
      case 'partner':
        return "Partner's company";
      case 'employer':
        return 'Employer (not pinned yet)';
      case 'personal':
        return 'Personal';
      default:
        return kind;
    }
  }

  /**
   * Turning the toggle on for the first time with an empty saved template starts the working copy
   * with the default template, so the super admin sees what will be saved.
   */
  onToggleProvisioning(isChecked: boolean): void {
    if (
      isChecked
      && !this.saved.can_provision_accounts
      && this.saved.rbac_permission_ids.length === 0
      && this.form.rbac_permission_ids.length === 0
    ) {
      this.form.rbac_permission_ids = [...(this.partner?.default_rbac_permission_ids || [])];
    }
  }

  /**
   * Open the permission selector in template mode.
   */
  onEditPermissions(): void {
    if ( !this.guardSuperAdmin() ) {
      return;
    }
    this.utils.showComponentDialog(
      RbacPermissionsSelectorComponent,
      { selected: [...this.form.rbac_permission_ids], purpose: 'template' },
      { width: '1200px' },
      (ids: number[]): void => {
        if ( typeof ids !== 'undefined' ) {
          this.form.rbac_permission_ids = ids;
        }
      }
    );
  }

  /**
   * Replace the working template with the default template.
   */
  onResetToDefault(): void {
    if ( !this.guardSuperAdmin() ) {
      return;
    }
    this.form.rbac_permission_ids = [...(this.partner?.default_rbac_permission_ids || [])];
  }

  /**
   * Validate and save the partner settings, confirming the changes that reach client accounts.
   */
  onSave(): void {
    if ( !this.guardSuperAdmin() ) {
      return;
    }
    if ( this.inProgress || !this.partner ) {
      return;
    }

    const roleName: string = (this.form.partner_role_name || '').trim();
    const ownAccountId: number | null = this.normaliseAccountId(this.form.partner_account_id);

    if ( this.form.can_provision_accounts ) {
      if ( roleName.length < 1 || roleName.length > 100 ) {
        this.utils.showFormValidationError('Please enter a partner role name.');
        return;
      }
      if ( ownAccountId === null || !Number.isInteger(ownAccountId) || ownAccountId < 1 ) {
        this.utils.showFormValidationError("Please enter the partner's own account ID.");
        return;
      }
    }

    const doSave = (): void => {
      this.save(roleName, ownAccountId);
    };

    if ( !this.saved.can_provision_accounts && this.form.can_provision_accounts ) {
      this.utils.showModal(
        'Enable Partner Provisioning',
        'This user will be able to create client accounts and users through the API. Saving gives it the '
          + "'" + roleName + "' role in every client account it is linked to except its own account (#"
          + ownAccountId + '), approves its pending memberships there, and replaces its Administrators role '
          + 'wherever another administrator exists. Continue?',
        doSave
      );
      return;
    }

    if (
      this.saved.can_provision_accounts
      && this.form.can_provision_accounts
      && ownAccountId !== this.normaliseAccountId(this.saved.partner_account_id)
    ) {
      this.utils.showModal(
        'Change Own Account',
        "The previous own account becomes a client account and receives the '" + roleName
          + "' role on the next sync. Continue?",
        doSave
      );
      return;
    }

    doSave();
  }

  /**
   * Send the PUT request.
   */
  private save(roleName: string, ownAccountId: number | null): void {
    if ( this.inProgress ) {
      return;
    }
    this.inProgress = true;
    this.apiErrors = {};

    const body: { [key: string]: any } = {
      can_provision_accounts: this.form.can_provision_accounts,
      partner_role_name: roleName.length > 0 ? roleName : null,
      rbac_permission_ids: [...this.form.rbac_permission_ids]
    };
    // The own account field is prefilled from the employer, so it is only sent while the flag is on or
    // when the super admin edited it. An untouched prefill on a flag-off save is left out, so the API
    // keeps the stored pin instead of validating a value the super admin never set.
    if (
      this.form.can_provision_accounts
      || ownAccountId !== this.normaliseAccountId(this.saved.partner_account_id)
    ) {
      body['partner_account_id'] = ownAccountId;
    }

    this.api
      .makeRequest('put', `v2/admin/users/${this.userId}/partner`, body)
      .then((response: PartnerSettings): void => {
        this.applyResponse(response, true);
        this.utils.showToast(
          'Partner settings saved.' + (response.last_run?.status === 'queued' ? ' A sync has been queued.' : '')
        );
      })
      .catch((errorResponse: any): void => {
        this.utils.handleAPIErrors(errorResponse);
        this.applyFieldErrors(errorResponse?.error?.errors);
      })
      .finally((): void => {
        this.inProgress = false;
      });
  }

  /**
   * Queue a full partner sync.
   */
  onResync(): void {
    if ( !this.guardSuperAdmin() ) {
      return;
    }
    if ( this.isDirty ) {
      this.utils.showToast('Save your changes first.');
      return;
    }
    if ( this.inProgress || this.runActive ) {
      return;
    }
    this.inProgress = true;

    this.api
      .makeRequest('post', `v2/admin/users/${this.userId}/partner/sync`)
      .then((response: PartnerSettings): void => {
        this.applyResponse(response, true);
        this.utils.showToast('A sync has been queued.');
      })
      .catch((errorResponse: any): void => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally((): void => {
        this.inProgress = false;
      });
  }

  /**
   * Map the API's 422 field errors onto the form fields.
   */
  private applyFieldErrors(errors: any): void {
    if ( !errors ) {
      return;
    }
    const first = (key: string): string | null => {
      const messages: any = errors[key];
      if ( Array.isArray(messages) && messages.length > 0 ) {
        return String(messages[0]);
      }
      return typeof messages === 'string' ? messages : null;
    };

    // The 'api' error shows the message as a mat-error. Angular re-runs the validators when the
    // value changes, which clears it.
    const roleNameError: string | null = first('partner_role_name');
    if ( roleNameError && this.roleNameModel ) {
      this.apiErrors['partner_role_name'] = roleNameError;
      this.roleNameModel.control.setErrors({ ...(this.roleNameModel.control.errors || {}), api: true });
      this.roleNameModel.control.markAsTouched();
    }
    const ownAccountError: string | null = first('partner_account_id');
    if ( ownAccountError && this.ownAccountModel ) {
      this.apiErrors['partner_account_id'] = ownAccountError;
      this.ownAccountModel.control.setErrors({ ...(this.ownAccountModel.control.errors || {}), api: true });
      this.ownAccountModel.control.markAsTouched();
    }
  }

  /**
   * Normalise an account id input (number input values can be null, '' or a number).
   */
  private normaliseAccountId(value: any): number | null {
    if ( value === null || typeof value === 'undefined' || value === '' ) {
      return null;
    }
    const id: number = Number(value);
    return isNaN(id) ? null : id;
  }

  /**
   * Poll the settings every 3 seconds while the newest run is queued or running.
   */
  private schedulePoll(): void {
    this.stopPolling();
    if ( this.destroyed || !this.runActive ) {
      return;
    }
    if ( this.pollCount >= this.maxPolls ) {
      this.utils.showToast('The sync is still running. Reopen this tab to check again.');
      return;
    }
    this.pollTimer = setTimeout((): void => {
      this.pollTimer = null;
      this.pollCount++;
      this.api
        .makeRequest('get', `v2/admin/users/${this.userId}/partner`)
        .then((response: PartnerSettings): void => {
          if ( this.destroyed ) {
            return;
          }
          // Polls refresh the counts and the last run only; the working copy is left alone.
          this.partner = response;
          this.buildTotalsRows();
          this.schedulePoll();
        })
        .catch((errorResponse: any): void => {
          if ( this.destroyed ) {
            return;
          }
          this.utils.handleAPIErrors(errorResponse);
        });
    }, this.pollIntervalMs);
  }

  private stopPolling(): void {
    if ( this.pollTimer ) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * Build the totals table of the last sync run.
   */
  private buildTotalsRows(): void {
    const run: PartnerSyncRun = this.partner?.last_run;
    const totals: { [key: string]: any } = run?.summary?.totals;
    if ( !totals ) {
      this.totalsRows = [];
      return;
    }
    const n = (key: string): number => Number(totals[key] || 0);
    const skipped: { [key: string]: any } = totals['links_skipped'] || {};
    const s = (key: string): string => String(Number(skipped[key] || 0));

    this.totalsRows = [
      { label: 'Accounts processed', value: String(n('accounts_processed')) },
      { label: 'Roles created / restored', value: n('roles_created') + ' / ' + n('roles_restored') },
      { label: 'Roles renamed', value: String(n('roles_renamed')) },
      {
        label: 'Accounts with permission changes',
        value: n('accounts_with_permission_changes') + ' (+' + n('permissions_added') + ' / -' + n('permissions_removed') + ')'
      },
      { label: 'Role assigned to the partner', value: String(n('roles_assigned')) },
      { label: 'Administrators role swapped', value: String(n('administrators_swapped')) },
      { label: 'Swaps skipped (only administrator)', value: String(n('swaps_skipped_only_administrator')) },
      { label: 'Pending memberships approved', value: String(n('pending_links_approved')) },
      { label: 'Skipped: own account', value: s('own_account') },
      { label: 'Skipped: merged', value: s('merged') },
      { label: 'Skipped: deleted account', value: s('deleted_account') },
      { label: 'Skipped: internal', value: s('internal') },
      { label: 'Skipped: membership removed', value: s('link_deleted') },
      { label: 'Skipped: membership declined', value: s('link_declined') },
      { label: 'Accounts where the partner holds other roles', value: String(n('accounts_with_other_roles')) },
      { label: 'Failed', value: String(n('failed')) }
    ];
  }

  /**
   * The other role names of an account row, joined for display.
   */
  getOtherRoles(row: any): string {
    return Array.isArray(row?.other_roles) ? row.other_roles.join(', ') : '';
  }
}
